import React, { useState } from "react";
import { toast } from "sonner";
import {
    User,
    Mail,
    GraduationCap,
    BookOpen,
    Hash,
    MessageSquare,
    Loader2,
    CheckCircle2,
    ArrowRight,
    Sparkles,
} from "lucide-react";
import { API_BASE_URL } from "../config/apiConfig";

const inputClass =
    "w-full border border-slate-200 bg-white rounded-lg pl-10 pr-4 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition shadow-sm";

function Field({ icon: Icon, children }) {
    return (
        <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-indigo-400 pointer-events-none">
                <Icon size={15} />
            </div>
            {children}
        </div>
    );
}

export default function TeechaaWaitlistSection() {
    const [formData, setFormData] = useState({
        fullName: "",
        email: "",
        university: "",
        department: "",
        graduationYear: "",
        message: "",
    });

    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleChange = (e) =>
        setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/waitlist-university`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });
            if (!res.ok) throw new Error();
            setSubmitted(true);
        } catch (err) {
            console.log(err)
            toast.error("Something went wrong.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=DM+Serif+Display:ital@0;1&display=swap');

                .waitlist-section {
                    font-family: 'Sora', sans-serif;
                }

                .waitlist-section h2 {
                    font-family: 'DM Serif Display', serif;
                }

                .image-side {
                    position: relative;
                    overflow: hidden;
                }

                .image-side::after {
                    content: '';
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(
                        135deg,
                        rgba(99, 102, 241, 0.45) 0%,
                        rgba(30, 27, 75, 0.65) 100%
                    );
                }

                .image-side img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    object-position: center;
                    transition: transform 8s ease;
                }

                .image-side:hover img {
                    transform: scale(1.04);
                }

                .image-overlay-text {
                    position: absolute;
                    inset: 0;
                    z-index: 10;
                    display: flex;
                    flex-direction: column;
                    justify-content: flex-end;
                    padding: 2.5rem;
                }

                .stat-chip {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    background: rgba(99, 102, 241, 0.12);
                    backdrop-filter: blur(8px);
                    border: 1px solid rgba(99, 102, 241, 0.25);
                    border-radius: 999px;
                    padding: 5px 14px;
                    font-size: 12px;
                    color: #4f46e5;
                    margin-bottom: 1rem;
                    width: fit-content;
                }

                .stat-chip-light {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    background: rgba(255, 255, 255, 0.18);
                    backdrop-filter: blur(8px);
                    border: 1px solid rgba(255, 255, 255, 0.3);
                    border-radius: 999px;
                    padding: 5px 14px;
                    font-size: 12px;
                    color: white;
                    margin-bottom: 1rem;
                    width: fit-content;
                }

                .form-card {
                    background: #ffffff;
                    border: 1px solid rgba(99, 102, 241, 0.12);
                    box-shadow:
                        0 1px 3px rgba(0,0,0,0.04),
                        0 8px 32px rgba(99, 102, 241, 0.08),
                        0 24px 64px rgba(99, 102, 241, 0.06);
                }

                .submit-btn {
                    background: linear-gradient(135deg, #6366f1, #4f46e5);
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 15px rgba(99, 102, 241, 0.3);
                }

                .submit-btn:hover:not(:disabled) {
                    transform: translateY(-1px);
                    box-shadow: 0 6px 20px rgba(99, 102, 241, 0.45);
                }

                .submit-btn:disabled {
                    opacity: 0.7;
                }

                .divider-dot {
                    width: 4px;
                    height: 4px;
                    border-radius: 50%;
                    background: rgba(255, 255, 255, 0.4);
                }

                .page-bg {
                    background: linear-gradient(155deg, #f8f7ff 0%, #eef2ff 40%, #f0f4ff 100%);
                    position: relative;
                }

                .page-bg::before {
                    content: '';
                    position: absolute;
                    top: -10%;
                    right: -5%;
                    width: 500px;
                    height: 500px;
                    background: radial-gradient(circle, rgba(165, 180, 252, 0.18) 0%, transparent 70%);
                    pointer-events: none;
                }

                .page-bg::after {
                    content: '';
                    position: absolute;
                    bottom: -10%;
                    left: -5%;
                    width: 400px;
                    height: 400px;
                    background: radial-gradient(circle, rgba(196, 181, 253, 0.14) 0%, transparent 70%);
                    pointer-events: none;
                }

                @keyframes fadeUp {
                    from { opacity: 0; transform: translateY(16px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .fade-up {
                    animation: fadeUp 0.5s ease forwards;
                }

                .fade-up-delay {
                    animation: fadeUp 0.5s ease 0.15s forwards;
                    opacity: 0;
                }
            `}</style>

            <section
                className="waitlist-section page-bg min-h-screen flex items-stretch"
            >
                {/* Left — Image Side */}
                <div className="image-side hidden lg:block w-1/2 relative">
                    <img
                        src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=1200&q=80&auto=format&fit=crop"
                        alt="Students studying at university"
                    />
                    <div className="image-overlay-text">
                        <div className="stat-chip-light">
                            <Sparkles size={12} />
                            AI-Powered Academic Assistant
                        </div>

                        <h2
                            className="text-white mb-3"
                            style={{ fontSize: "2.4rem", lineHeight: 1.15, fontWeight: 400 }}
                        >
                            Study smarter,<br />
                            <em>not harder.</em>
                        </h2>

                        <p className="text-white/70 text-sm leading-relaxed max-w-xs">
                            Teechaa helps university students understand complex material,
                            ace exams, and stay ahead of their coursework.
                        </p>

                        <div className="flex items-center gap-3 mt-5">
                            {["200+ Universities", "Early Access", "Free to Join"].map((label, i) => (
                                <React.Fragment key={label}>
                                    {i > 0 && <div className="divider-dot" />}
                                    <span className="text-white/60 text-xs">{label}</span>
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right — Form Side */}
                <div
                    className="w-full lg:w-1/2 flex items-center justify-center px-6 py-16 relative z-10"
                >
                    <div className="w-full max-w-md">
                        {/* Mobile-only heading */}
                        <div className="lg:hidden text-center mb-10">
                            <div className="stat-chip mx-auto">
                                <Sparkles size={12} />
                                AI-Powered Academic Assistant
                            </div>
                            <h2
                                className="text-slate-800 mt-2"
                                style={{ fontSize: "2.2rem", lineHeight: 1.2 }}
                            >
                                Study smarter,<br />
                                <em>not harder.</em>
                            </h2>
                        </div>

                        <div className="mb-8 fade-up">
                            <p className="text-indigo-500 text-xs font-medium tracking-widest uppercase mb-2">
                                Early Access
                            </p>
                            <h3
                                className="text-slate-800 font-semibold"
                                style={{ fontSize: "1.6rem", fontFamily: "'Sora', sans-serif" }}
                            >
                                Join the Waitlist
                            </h3>
                            <p className="text-slate-400 text-sm mt-1">
                                Be first when Teechaa launches at your university.
                            </p>
                        </div>

                        {submitted ? (
                            <div className="form-card rounded-2xl p-8 text-center fade-up">
                                <div
                                    className="mx-auto mb-5 flex items-center justify-center rounded-full"
                                    style={{
                                        width: 56,
                                        height: 56,
                                        background: "rgba(99,102,241,0.1)",
                                        border: "1px solid rgba(99,102,241,0.2)",
                                    }}
                                >
                                    <CheckCircle2 size={26} className="text-indigo-500" />
                                </div>
                                <h3 className="text-slate-800 text-xl font-semibold mb-2">
                                    You're on the list!
                                </h3>
                                <p className="text-slate-400 text-sm">
                                    We'll notify you when Teechaa launches at your university.
                                </p>
                            </div>
                        ) : (
                            <form
                                onSubmit={handleSubmit}
                                className="form-card rounded-2xl p-6 space-y-3 fade-up-delay"
                            >
                                <Field icon={User}>
                                    <input
                                        name="fullName"
                                        placeholder="Full Name"
                                        required
                                        value={formData.fullName}
                                        onChange={handleChange}
                                        className={inputClass}
                                    />
                                </Field>

                                <Field icon={Mail}>
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder="Email address"
                                        required
                                        value={formData.email}
                                        onChange={handleChange}
                                        className={inputClass}
                                    />
                                </Field>

                                <div className="grid grid-cols-2 gap-3">
                                    <Field icon={GraduationCap}>
                                        <input
                                            name="university"
                                            placeholder="University"
                                            required
                                            value={formData.university}
                                            onChange={handleChange}
                                            className={inputClass}
                                        />
                                    </Field>

                                    <Field icon={BookOpen}>
                                        <input
                                            name="department"
                                            placeholder="Department"
                                            required
                                            value={formData.department}
                                            onChange={handleChange}
                                            className={inputClass}
                                        />
                                    </Field>
                                </div>

                                <Field icon={Hash}>
                                    <input
                                        type="number"
                                        name="graduationYear"
                                        placeholder="Level (e.g. 200)"
                                        required
                                        value={formData.graduationYear}
                                        onChange={handleChange}
                                        className={inputClass}
                                    />
                                </Field>

                                <Field icon={MessageSquare}>
                                    <textarea
                                        name="message"
                                        placeholder="Why are you interested? (Optional)"
                                        value={formData.message}
                                        onChange={handleChange}
                                        rows={3}
                                        className={`${inputClass} resize-none`}
                                        style={{ paddingTop: "10px" }}
                                    />
                                </Field>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="submit-btn w-full text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 text-sm mt-1"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 size={15} className="animate-spin" />
                                            Joining...
                                        </>
                                    ) : (
                                        <>
                                            Join the Waitlist
                                            <ArrowRight size={15} />
                                        </>
                                    )}
                                </button>

                                <p className="text-center text-slate-400 text-xs pt-1">
                                    No spam. Unsubscribe anytime.
                                </p>
                            </form>
                        )}
                    </div>
                </div>
            </section>
        </>
    );
}